import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';

import {
  AbstractControl,
  FormGroup,
  UntypedFormBuilder,
  UntypedFormGroup,
  ValidationErrors,
  ValidatorFn,
} from '@angular/forms';
import { MatOptionSelectionChange } from '@angular/material/core';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { ActivatedRoute, Router } from '@angular/router';

import { TranslocoService, translate, translateObject } from '@jsverse/transloco';
import { BehaviorSubject, Observable, Subject } from 'rxjs';

import { map, startWith, takeUntil, tap } from 'rxjs/operators';

import { SimpleDialogButtonColor } from 'app/components/simple-dialog.component';
import { FieldConstants } from 'app/constants/constants';
import { FullStoryService, PageNames } from 'app/modules/full-story/services/full-story.service';
import { AnalyticsService, TimerFn } from 'app/providers/analytics.service';
import { SemantriaPreferences } from 'app/providers/bridge-api/preferences.service';
import { GroupService } from 'app/providers/semantria-api/group.service';
import {
  AccountMetadata,
  StorageAccountService,
} from 'app/providers/storage-api/blender-account.service';
import { CreateFieldBulkData, Field, FieldService } from 'app/providers/storage-api/field.service';
import { Upload, UploadColumn, UploadService } from 'app/providers/storage-api/upload.service';
import { AlertService } from 'app/services/alert.service';
import { DialogService } from 'app/services/dialog.service';
import {
  EnableClassTelemetry,
  EnableComponentTelemetry,
  injectComponentTelemetryService,
} from 'app/services/otel.service';

import { TimezoneService } from 'app/services/timezone.service';
import {
  FIELD_TYPES,
  FieldTypeDialogComponent,
  FieldTypeDialogOptions,
} from '../../components/field-type-dialog/field-type-dialog.component';
import {
  TxEditorDialogComponent,
  TxEditorDialogOptions,
} from '../../components/tx-editor-dialog/tx-editor-dialog.component';

export interface FieldGroup {
  label: string;
  fields: string[];
}

export const _filter = (opt: string[], value: string): string[] => {
  const filterValue = value.toLowerCase();

  return opt.filter((item) => item.toLowerCase().indexOf(filterValue) === 0);
};

@EnableClassTelemetry()
@Component({
  selector: 'app-upload-map',
  templateUrl: './upload-map.component.html',
  styleUrls: ['./upload-map.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    { provide: MAT_FORM_FIELD_DEFAULT_OPTIONS, useValue: { subscriptSizing: 'dynamic' } },
  ],
  standalone: false,
})
export class UploadMapComponent implements OnInit, OnDestroy {
  @EnableComponentTelemetry() otelService = injectComponentTelemetryService();
  @ViewChild('autoIDToggle') autoIDToggle: ElementRef;
  static UPLOAD_WAIT_SECONDS = 1;
  static MAX_SAMPLE_LENGTH = 72;
  static STANDARD_FIELDS = [
    'Creator',
    'Date',
    'ID',
    'Language',
    'Location ID',
    'Parent',
    'Publisher',
    'Source',
    'Sections',
    'VectorMetrics',
    'ScalarMetrics',
    'Tags',
    'Text',
    'Title',
    'Type',
    'URL',
    'AudioPath',
  ];
  static FIELD_NAME_MAP = {
    Publisher: 'publisher',
    Language: 'language',
    Parent: 'parentId',
    'Location ID': 'locationId',
    Tags: 'tags',
    URL: 'url',
    Text: 'text',
    Creator: 'creator',
    Source: 'source',
    Sections: 'sections',
    VectorMetrics: 'vectorMetrics',
    ScalarMetrics: 'scalarMetrics',
    Title: 'title',
    Date: 'createdAt',
    Type: 'type',
    ID: 'id',
    AudioPath: 'audioPath',
  };
  static FIELD_REVERSE_MAP = {};
  static FIELD_LOWER_MAP = {};
  static FIELD_SET = new Set();

  public projectId: string | null = null;
  public uploadId: string | null = null;
  public collectionName: string | null = null;
  public collectionDisplayName: string | null = null;
  public upload: Upload | null = null;
  private accountMetadata: AccountMetadata | null = null;
  public fieldTypeMap: Map<string, string> = new Map();
  public fieldTypeFilterableMap: Map<string, boolean> = new Map();
  public metadataFieldNames: string[] = [];
  public metadataNormalizedNames: string[] = [];

  public fieldForm: UntypedFormGroup = this.fb.group({}); // we'll configure this later

  public fieldGroups: FieldGroup[] = [
    {
      label: '',
      fields: UploadMapComponent.STANDARD_FIELDS,
    },
    {
      label: '',
      fields: [], // we'll fill this in later
    },
  ];

  public encodingsUsed: { title: string; id: string }[];
  public fieldGroupOptions: Observable<FieldGroup[]>[] = [];
  public pendingFieldTypeMap: Map<string, string> = new Map();

  public samples: string[][] = Array<Array<string>>();
  public autoFoundFields: string[] = [];
  public isLoadingUpload$ = new BehaviorSubject<boolean>(true);
  public isLoadingConfirm$ = new BehaviorSubject<boolean>(false);
  public isError = false;
  public isReady = false;
  public showEncodingOptions = false;
  public showDialectOptions = false;
  public showFirstRowOptions = false;
  public autoCreateIds = false;
  public groupByColumn: UploadColumn;
  public autoCompleteOpened: string;
  public fileType: string;
  private reportTiming: TimerFn;
  public uploadTimezone: string;
  public uploadTimezoneOffset: string;
  public uploadTimezoneTooltip: string;
  public hasDateColumn = false;

  public showTimezoneModal = false;

  allFields: Field[] = [];

  private unsub$ = new Subject<void>();

  public static initialize() {
    Object.keys(this.FIELD_NAME_MAP).forEach((key) => {
      const value = this.FIELD_NAME_MAP[key];
      this.FIELD_REVERSE_MAP[value] = key;
      this.FIELD_LOWER_MAP[key.toLowerCase()] = key;
      this.FIELD_SET.add(key.toLowerCase());
      this.FIELD_SET.add(value.toLowerCase());
    });
  }

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private fb: UntypedFormBuilder,
    private element: ElementRef,
    private changeDetectorRef: ChangeDetectorRef,
    private alertService: AlertService,
    private dialogService: DialogService,
    private uploadService: UploadService,
    private blenderAccountService: StorageAccountService,
    private fullStoryService: FullStoryService,
    private analyticsService: AnalyticsService,
    private groupService: GroupService,
    private fieldService: FieldService,
    private preferencesService: SemantriaPreferences,
    private timezoneService: TimezoneService,
    private translocoService: TranslocoService
  ) {}

  async ngOnInit() {
    this.translocoService
      .selectTranslateObject('upload-map')
      .pipe(takeUntil(this.unsub$))
      .subscribe((t: { [key: string]: string }) => {
        this.fieldGroups[0].label = t['standard-fields'];
        this.fieldGroups[1].label = t['custom-fields'];
        this.encodingsUsed = [
          {
            title: t['utf-8'],
            id: 'UTF-8',
          },
          {
            title: t['windows-1252'],
            id: 'Windows-1252',
          },
          {
            title: t['none'],
            id: 'None',
          },
        ];
      });
    const params = this.route.snapshot.paramMap;
    this.projectId = params.get('projectId');
    this.collectionName = params.get('collectionName');
    this.uploadId = params.get('uploadId');
    const queryParams = this.route.snapshot.queryParamMap;
    this.collectionDisplayName = queryParams.get('displayName');
    this.allFields = (await this.fieldService.getFields()).items;

    this.reportTiming = this.analyticsService.createTimer('Uploader');
    this.fullStoryService.setPageVars(PageNames.upload_mapping, {
      projectId: this.projectId,
      collectionId: this.collectionName,
      uploadId: this.uploadId,
    });
    const allUploads = await this.uploadService.getAllUploads(this.projectId);
    const uploads = allUploads.items.filter((u) => u.metadata.timezone);
    uploads.sort(function compare(a, b) {
      const dateA = a.created_at.getTime();
      const dateB = b.created_at.getTime();
      return dateB - dateA;
    });
    if (this.preferencesService) {
      const preferences = await this.preferencesService.getPreferences();
      let lastUsedTimezone;
      if (uploads.length) {
        lastUsedTimezone = uploads[0].metadata.timezone;
      }

      this.uploadTimezone = lastUsedTimezone || preferences.timezone;
      this.setTimezoneOffset();
    }

    const groupId = params.get('groupId');
    await this.groupService.refreshAndSelectActiveGroup(groupId);

    this.blenderAccountService.groupFieldsSub
      .takeUntil(this.unsub$)
      .subscribe((accountMetadata) => {
        this.accountMetadata = accountMetadata;
        // fill in the fieldTypeMap so we can quickly see if a name is defined
        this.accountMetadata.metadata.forEach((f) => {
          let fieldType = f.fieldType;
          if (f.fieldType === 'long' && f.metadata?.rating) {
            fieldType = 'rating';
          }
          if (f.fieldType === 'double' && f.metadata?.weight) {
            fieldType = 'weight';
          }
          this.fieldTypeMap.set(f.label, fieldType);
          this.fieldTypeMap.set(f.fieldName, fieldType);
          this.metadataFieldNames.push(f.label);
          this.metadataNormalizedNames.push(f.normalizedName);
        });
        // add the list of fields to autocomplete datastructure
        this.fieldGroups[1].fields = this.accountMetadata.metadata
          .filter((f) => f.fieldName.startsWith(FieldConstants.MD_))
          .map((f) => f.label);
        // and make sure they're sorted
        this.fieldGroups.forEach((fg) => fg.fields.sort());
      });

    this.refresh();
  }

  ngOnDestroy(): void {
    this.unsub$.next();
    this.unsub$.complete();
  }

  public async refresh(reinspectedUpload?: Upload): Promise<void> {
    this.isLoadingUpload$.next(true);
    this.isError = false;
    this.fieldGroupOptions = [];

    if (reinspectedUpload) {
      this.upload = reinspectedUpload;
    } else {
      try {
        this.upload = await this.uploadService.getUploadById(this.uploadId, this.projectId);
      } catch (err) {
        this.alertService.showErrorToast(translate('upload-map.failed-to-get-upload'), err);
        return;
      }
    }

    if (this.upload.state === 'ERROR') {
      this.isLoadingUpload$.next(false);
      this.isError = true;
      return;
    }
    if (this.upload.state !== 'INSPECTED') {
      setTimeout(this.refresh.bind(this), UploadMapComponent.UPLOAD_WAIT_SECONDS * 1000);
      return;
    }

    // the upload has the internal field names (createdAt) where we want to see the display versios (Date)
    await this._convertFieldNamesToDisplayValues(this.upload);

    // we need to set up an autocomplete for each of the metadata fields
    const controlsConfig = {};
    for (let i = 0; i < this.upload.metadata.columns.length; i++) {
      const control = this.fb.control(this.upload.metadata.columns[i].field_name ?? '');
      controlsConfig[`fieldGroup${i}`] = control;
    }
    this.fieldForm = this.fb.group(controlsConfig, { validators: this.fieldNamesValidator });
    for (let i = 0; i < this.upload.metadata.columns.length; i++) {
      const fg = this.getColumnField(i);
      if (fg !== undefined) {
        const fgo = fg.valueChanges.pipe(
          startWith(fg.value),
          map((value) => (value = value.trim())),
          tap((value) => (this.upload.metadata.columns[i].field_name = value)),
          map((value) => this._filterField(value, i))
        );
        this.fieldGroupOptions.push(fgo);
      }
    }

    this.fileType = this.upload.file_name.split('.').pop().toUpperCase();

    // encoding stuff is only shown for non-excel files
    const isExcel = this.upload.file_name.toLowerCase().indexOf('.xls') !== -1;
    const isJson = this.upload.file_name.toLowerCase().indexOf('.json') !== -1;
    this.showEncodingOptions = !isExcel;
    this.showDialectOptions = !isExcel;
    this.showFirstRowOptions = !isJson;

    this.setAutoFields();

    // populate the samples matrix
    this.samples = Array<Array<string>>();
    for (let rowIdx = 0; ; rowIdx++) {
      const row = Array<string>();
      let found_any = false;
      for (let i = 0; i < this.upload.metadata.columns.length; i++) {
        const column = this.upload.metadata.columns[i];
        if (rowIdx < column.samples.length) {
          let value = column.samples[rowIdx];
          if (value.length > UploadMapComponent.MAX_SAMPLE_LENGTH) {
            value = value.substr(0, UploadMapComponent.MAX_SAMPLE_LENGTH) + '...';
          }
          row.push(value);
          found_any = true;
        } else {
          row.push('');
        }
      }
      if (!found_any) {
        break;
      }
      this.samples.push(row);
    }

    setTimeout(() => {
      this.element.nativeElement.querySelector('input.mat-mdc-input-element').focus();
    }, 0);

    // done!
    this.isLoadingUpload$.next(false);

    const loadTime = this.reportTiming('upload_mapping_loaded');
    this.fullStoryService.setPageVars(PageNames.upload_mapping, {
      loadTime,
    });
  }

  // for use with the autocomplete
  private _filterField(value: string, formFieldIdx): FieldGroup[] {
    if (value) {
      //Switch auto-create ID's toggle off if ID added to column
      if (value.toLowerCase() === 'id') {
        this.autoCreateIds = false;
      }

      const targetColumn = this.upload.metadata.columns[formFieldIdx];
      if (targetColumn.field_name) {
        // ensure correct case of standard field
        if (this.isStandardFieldName(targetColumn.field_name)) {
          targetColumn.field_name =
            UploadMapComponent.FIELD_LOWER_MAP[targetColumn.field_name.toLowerCase()];
        }
      }

      return this.fieldGroups
        .map((group) => ({ label: group.label, fields: _filter(group.fields, value) }))
        .filter((group) => group.fields.length > 0);
    }
    return this.fieldGroups;
  }

  private fieldNamesValidator: ValidatorFn = (formGroup: FormGroup): ValidationErrors | null => {
    const controls = Object.values(formGroup.controls);

    // 1. Clear the custom error from all controls to start fresh
    controls.forEach((ctrl) => this.removeControlError(ctrl, 'hasDuplicate'));
    // 2. Group controls by their normalized value
    const valueGroups = new Map<string, AbstractControl[]>();

    controls.forEach((ctrl) => {
      const val = ctrl.value?.toString().trim().toLowerCase();
      if (val) {
        const group = valueGroups.get(val) || [];
        valueGroups.set(val, [...group, ctrl]);
      }
    });

    // 3. Mark duplicates and track if any exist
    let isGroupInvalid = false;

    valueGroups.forEach((instances) => {
      if (instances.length > 1) {
        isGroupInvalid = true;
        instances.forEach((ctrl) => {
          ctrl.setErrors({ ...ctrl.errors, hasDuplicate: true });
        });
      }
    });

    return isGroupInvalid ? { hasDuplicate: true } : null;
  };

  /**
   * Utility to safely remove a specific key from a control's errors object
   */
  private removeControlError(control: AbstractControl, errorKey: string): void {
    const errors = control.errors;
    if (!errors || !errors[errorKey]) {
      return;
    }

    const { [errorKey]: _, ...remainingErrors } = errors;
    const hasOtherErrors = Object.keys(remainingErrors).length > 0;

    control.setErrors(hasOtherErrors ? remainingErrors : null);
  }

  private _getCustomFieldNameColumns(): UploadColumn[] {
    return this.upload.metadata.columns.filter((column) => {
      if (column.column_name.includes('|')) {
        //need to escape all pipes
        column.column_name = column.column_name.replace(/\|/g, '\\|');
      }
      return column.field_name && this.isCustomFieldName(column.field_name);
    });
  }

  //Clear any ID field titles if auto-id switched on
  public onAutoIDToggle(isToggled: boolean) {
    if (isToggled) {
      this.autoCreateIds = true;
      if (this.isFieldSet('id')) {
        for (let i = 0; i < this.upload.metadata.columns.length; i++) {
          const column = this.upload.metadata.columns[i];
          if (column.field_name && 'id' === column.field_name.toLowerCase()) {
            this.getColumnField(i).setValue('');
          }
        }
      }
    } else {
      this.autoCreateIds = false;
    }
  }

  // if the user decides something doesn't look right about the encoding they
  // can use this method to re-inspect with a different encoding
  public async reinspectWithEncoding(id: string): Promise<void> {
    let encoding: string;
    switch (id) {
      case 'UTF-8':
        encoding = 'UTF-8';
        break;
      case 'Windows-1252':
        encoding = 'Windows-1252';
        break;
      //No encoding
      case 'None':
        encoding = '';
    }
    const uploadCopy = <Upload>JSON.parse(JSON.stringify(this.upload));
    uploadCopy.metadata.encoding = encoding;
    await this.reinspect(uploadCopy);
  }

  public async reinspectWithDelimiter(isToggled: boolean): Promise<void> {
    let delimiter: string;
    isToggled ? (delimiter = '\t') : (delimiter = ',');
    const uploadCopy = <Upload>JSON.parse(JSON.stringify(this.upload));
    uploadCopy.metadata.delimiter = delimiter;
    await this.reinspect(uploadCopy);
  }

  public async reinspectWithFirstRowHeaders(isToggled: boolean): Promise<void> {
    const columnName = !isToggled;
    const uploadCopy = <Upload>JSON.parse(JSON.stringify(this.upload));
    uploadCopy.metadata.firstRecordAsHeader = columnName;
    await this.reinspect(uploadCopy);
  }

  private async reinspect(upload: Upload): Promise<void> {
    try {
      await this._convertFieldNamesToBackendValues(upload);
      this.autoFoundFields = [];
      const reinspectedUpload = await this.uploadService.updateMatadata(upload, true);
      await this.refresh(reinspectedUpload);
    } catch (error) {
      this._showError('reinspect', error);
    }
  }

  // when happy, this will send the mappings to the server to start the loading
  // and send the user back to the collections page
  public async uploadMetadata(): Promise<void> {
    try {
      this.isLoadingConfirm$.next(true);
      const newFieldNames = this._getCustomFieldNameColumns()
        .filter((column) => this.isNewCustomField(column.field_name))
        .map((column) => column.field_name);
      const createFieldBulkDatas: CreateFieldBulkData[] = [];
      const groupId = this.groupService.getSelectedGroupId();
      for (const fieldName of newFieldNames) {
        const metadata: { [key: string]: any } = {
          filterable: this.fieldTypeFilterableMap.get(fieldName),
        };
        let fieldType = this.fieldTypeMap.get(fieldName);
        if (fieldType === 'rating') {
          fieldType = 'long';
          metadata.rating = true;
        }
        if (fieldType === 'weight') {
          fieldType = 'double';
          metadata.weight = true;
        }
        createFieldBulkDatas.push({
          label: fieldName,
          fieldType,
          description: fieldName,
          metadata,
        });
      }

      const createFieldBulkResult = await this.fieldService.createFields(
        groupId,
        createFieldBulkDatas
      );
      for (const item of createFieldBulkResult.items) {
        this.fieldTypeMap.set(item.field.label, item.field.label);
        this.fieldTypeMap.set(item.field.fieldName, item.field.fieldType);
      }

      this.blenderAccountService.groupFieldsSub.load(true);
      if (
        this.groupByColumn &&
        this.groupByColumn.field_name &&
        this.isCustomFieldName(this.groupByColumn.field_name)
      ) {
        this.upload.metadata.groupby = this.groupByColumn.field_name;
      }

      const uploadCopy = <Upload>JSON.parse(JSON.stringify(this.upload));
      await this._convertFieldNamesToBackendValues(uploadCopy);

      if (!this.isFieldSet('ID')) {
        uploadCopy.metadata.columns.push({
          field_name: 'id',
          column_name: '-|uuid',
          samples: [],
        });
      }

      await this.uploadService.updateMatadata(uploadCopy, false, this.uploadTimezone);

      this.router.navigate([
        `groups/${this.groupService.getSelectedGroupId(true)}/projects/${this.projectId}`,
      ]);
    } catch (error) {
      this._showError('uploadMetadata', error);
      this.isLoadingConfirm$.next(false);
    }
  }

  private _showError(name: string, error: any): void {
    const message = translate('upload-map.something-went-wrong');
    this.alertService.showErrorToast(message, error);
  }

  private async _convertFieldNamesToDisplayValues(upload: Upload) {
    const fieldMap = {};
    (await this.fieldService.getFields()).items.forEach(
      (f) => (fieldMap[`metadata.${f.fieldName}`] = f.label)
    );
    this._mapFieldNames(
      upload,
      UploadMapComponent.FIELD_REVERSE_MAP,
      (fieldName: string) => fieldMap[fieldName]
    );
  }
  private async _convertFieldNamesToBackendValues(upload: Upload) {
    // swap out display names, add "metadata."
    const fieldMap = {};
    (await this.fieldService.getFields()).items.forEach((f) => (fieldMap[f.label] = f.fieldName));
    this._mapFieldNames(
      upload,
      UploadMapComponent.FIELD_NAME_MAP,
      (label: string) => 'metadata.' + fieldMap[label]
    );
  }

  private _mapFieldNames(upload: Upload, nameMap: object, unknownFn) {
    upload.metadata.columns.forEach((column) => {
      if (!column.field_name) {
        return;
      }
      if (nameMap[column.field_name]) {
        column.field_name = nameMap[column.field_name];
      } else {
        column.field_name = unknownFn(column.field_name);
      }
      let normalizedFieldName = column.field_name;
      if (normalizedFieldName?.startsWith('metadata.')) {
        normalizedFieldName = normalizedFieldName.substring(9);
        if (normalizedFieldName === 'undefined') {
          column.field_name = '';
        }
      }
      const fieldType = this.fieldTypeMap.get(normalizedFieldName);
      if (fieldType === 'date' && !column.transform) {
        column.transform = `any-date:'{"timezone":"${this.uploadTimezone}"}'`;
      }
    });
  }

  // look for columns named the same as a field.  if found
  // auto populate them for the user
  public setAutoFields(): void {
    // first we'll save the current field names
    const existingFields = new Set<string>();
    for (let i = this.upload.metadata.columns.length - 1; i >= 0; i--) {
      const column = this.upload.metadata.columns[i];
      if (column.field_name) {
        existingFields.add(column.field_name);
      }
    }
    for (let i = this.upload.metadata.columns.length - 1; i >= 0; i--) {
      const column = this.upload.metadata.columns[i];
      const auto_field = this.checkForAutoField(column.column_name);
      if (auto_field && !column.field_name && !existingFields.has(auto_field)) {
        this.autoFoundFields.push(column.column_name);
        setTimeout(() => this.getColumnField(i).setValue(auto_field));
      }
    }
  }

  public checkForAutoField(name: string): string {
    if (!name || FieldService.isCustomField(name)) {
      return null;
    }
    name = name.replace('\ufeff', '');
    name = name.toLowerCase();
    const matches = UploadMapComponent.STANDARD_FIELDS.filter((fld) => fld.toLowerCase() === name);
    if (matches.length > 0) {
      return matches[0];
    }
    if (this.accountMetadata) {
      const std_matches = this.accountMetadata.metadata
        .filter(
          (fld) =>
            fld.fieldName.toLowerCase() === name ||
            fld.label.toLowerCase() === name ||
            fld.description.toLowerCase() === name
        )
        .map((fld) => fld.label);
      if (std_matches.length > 0) {
        return std_matches[0];
      }
    }
    return undefined;
  }

  // count of columns with no field name set
  public notIncludedCount(): number {
    return this.upload.metadata.columns
      .map((column) => (column.field_name ? 0 : 1))
      .reduce((a, b) => a + b, 0);
  }

  // count of columns that were auto found
  public autoFoundCount(): number {
    // side-effect we'll check to see if any were removed by the user and not count those
    this.autoFoundFields = this.autoFoundFields.filter((field_name) => this.isFieldSet(field_name));
    return this.autoFoundFields.length;
  }

  // ccount of columns that the user manually set
  public manualCount(): number {
    const mappedCount = this.upload.metadata.columns.length - this.notIncludedCount();
    return mappedCount - this.autoFoundCount();
  }

  // the id and text fields are required for any mapping
  public areRequiredFieldsMapped(): boolean {
    return (
      (this.autoCreateIds || this.isFieldSet('ID')) &&
      (this.isFieldSet('Text') || this.isFieldSet('Sections'))
    );
  }

  // test to make sure there are no invalid fields specified
  public areAllFieldsValid(): boolean {
    for (let i = 0; i < this.upload.metadata.columns.length; i++) {
      const column = this.upload.metadata.columns[i];
      if (!this.isOKFieldName(column.field_name) || !this.isUniqueFieldName(column.field_name)) {
        return false;
      }
    }
    return true;
  }

  // test to see if mapping is ready to go
  public isMappingComplete(): boolean {
    if (!this.areRequiredFieldsMapped()) {
      return false;
    }
    if (!this.areAllFieldsValid()) {
      return false;
    }
    if (!this.areAllCustomTypesDefined()) {
      return false;
    }
    if (this.fieldForm.invalid) {
      return false;
    }
    return true;
  }

  // look up the type for a custom field
  public getFieldType(name: string): string {
    if (this.fieldTypeMap.has(name)) {
      const value = this.fieldTypeMap.get(name);
      for (const opt of FIELD_TYPES) {
        if (opt.value === value) {
          return translate(`upload-map.${opt.label.toLowerCase()}`);
        }
      }
      return value;
    }
    return '';
  }

  // test to see if this is a custom field
  public isCustomFieldName(name: string): boolean {
    return (
      this.isOKFieldName(name) && this.isUniqueFieldName(name) && !this.isStandardFieldName(name)
    );
  }

  // test to see if this is a new custom field
  public isNewCustomField(name: string): boolean {
    return !this.fieldTypeMap.has(name) || this.pendingFieldTypeMap.has(name);
  }

  // test to see if this is a standard field
  public isStandardFieldName(name: string): boolean {
    if (!this.isOKFieldName(name)) {
      return false;
    }
    return UploadMapComponent.FIELD_SET.has(name.toLowerCase());
  }

  // is the specified field set on a column
  public isFieldSet(field: string): boolean {
    for (let i = 0; i < this.upload.metadata.columns.length; i++) {
      const column = this.upload.metadata.columns[i];
      if (column.field_name && column.field_name.toLowerCase() === field.toLowerCase()) {
        return true;
      }
    }
    return false;
  }

  // check to see if field name is valid
  public isOKFieldName(name: string): boolean {
    if (!name) {
      return true;
    }
    return (
      !FieldService.isCustomField(name) &&
      FieldService.FIELD_VALID_RE.test(name) &&
      !this.accountMetadata.metadata.find(
        (f) => !FieldService.FIELD_VALID_CUSTOM_RE.test(f.fieldName) && f.label === name
      )
    );
  }

  public getInvalidNameText(name: string) {
    const t = translateObject('upload-map');
    const invalidField = this.accountMetadata.metadata.find(
      (f) => !FieldService.FIELD_VALID_CUSTOM_RE.test(f.fieldName) && f.label === name
    );
    if (
      invalidField?.fieldName.startsWith(FieldConstants.A_) ||
      name.toLowerCase().startsWith(FieldConstants.A_)
    ) {
      return t['invalid-name_alias'];
    } else if (
      invalidField?.fieldName.startsWith(FieldConstants.CL_) ||
      name.toLowerCase().startsWith(FieldConstants.CL_)
    ) {
      return t['invalid-name_calculated'];
    } else {
      return t['invalid-name_non-standard'];
    }
  }

  public isUniqueFieldName(name: string): boolean {
    if (!name) {
      return true;
    }
    const normalizedName = name.toLowerCase().replace(FieldService.NORMALIZED_NAME_RE, '');
    if (this.metadataNormalizedNames.indexOf(normalizedName) >= 0) {
      if (this.metadataFieldNames.indexOf(name) < 0) {
        return false;
      }
    }

    return true;
  }

  public onEditTransformation(column: UploadColumn) {
    const isGroupby = this.groupByColumn && this.groupByColumn === column;
    this.dialogService
      .showDialog<TxEditorDialogComponent, TxEditorDialogOptions>(
        TxEditorDialogComponent,
        { column, isGroupby },
        {},
        false
      )
      .afterClosed()
      .subscribe((result) => {
        if (result) {
          column.transform = result.transform;
          if (result.isGroupby) {
            this.groupByColumn = column;
          }
        }
      });
  }

  // open a dialog for the user to pick data types for any new custom fields
  public chooseFieldTypes(fieldName: string): void {
    let fieldType = null;
    if (this.fieldTypeMap.has(fieldName)) {
      fieldType = this.fieldTypeMap.get(fieldName);
    }
    this.dialogService
      .showDialog<FieldTypeDialogComponent, FieldTypeDialogOptions>(
        FieldTypeDialogComponent,
        {
          fieldName,
          fieldType,
          filterable: !!this.fieldTypeFilterableMap.get(fieldName),
          allFields: this.allFields,
        },
        {},
        false
      )
      .afterClosed()
      .subscribe((result) => {
        if (result) {
          const resultFieldType = result.metadataField.fieldType;
          const originalFieldName = result.metadataField.originalFieldName;
          const resultFieldName = result.metadataField.fieldName;
          const filterable = result.metadataField.filterable;

          if (fieldName !== originalFieldName) {
            const idx = this.upload.metadata.columns.findIndex(
              (c) => c.field_name === originalFieldName
            );
            this.getColumnField(idx).setValue(fieldName);
          }

          if (this.fieldTypeMap.has(originalFieldName)) {
            this.fieldTypeMap.delete(originalFieldName);
          }
          this.fieldTypeMap.set(resultFieldName, resultFieldType);
          this.fieldTypeFilterableMap.set(fieldName, filterable);
          this.pendingFieldTypeMap.set(fieldName, fieldType);
          this.fieldGroups[1].fields.sort();
        }
        this.changeDetectorRef.detectChanges();
      });
  }

  // test if all custom fields have their data types defined
  public areAllCustomTypesDefined(): boolean {
    for (let i = 0; i < this.upload.metadata.columns.length; i++) {
      const column = this.upload.metadata.columns[i];
      if (!column.field_name) {
        continue;
      }
      if (this.isCustomFieldName(column.field_name) && !this.getFieldType(column.field_name)) {
        return false;
      }
    }
    return true;
  }

  // fired when an autocomplete is selected
  public onACSelectionChange(
    event: MatOptionSelectionChange,
    option: string,
    uploadColumn: UploadColumn
  ): void {
    // if source not selected this event is deselecting another one
    if (!event.source.selected) {
      return;
    }

    // if it was an autofield it isn't any more
    this.autoFoundFields = this.autoFoundFields.filter(
      (field_name) => field_name.toLowerCase() !== option.toLowerCase()
    );

    // clear out any old assignment
    this.upload.metadata.columns.forEach((c, idx) => {
      if (c !== uploadColumn && c.field_name === option) {
        this.getColumnField(idx).setValue('');
      }
    });

    //Switch auto-create ID's toggle off if ID added to column
    if (option === 'ID') {
      this.autoCreateIds = false;
      this.autoIDToggle.nativeElement.isToggled = false;
    }

    this.hasDateColumn = false;
    const fieldType = this.fieldTypeMap.get(option);
    if (option === 'Date' || fieldType === 'date') {
      this.hasDateColumn = true;
    } else {
      for (const column of this.upload.metadata.columns) {
        const fieldTypeByFieldName = this.fieldTypeMap.get(column.field_name);
        if (column.field_name === 'Date' || fieldTypeByFieldName === 'date') {
          this.hasDateColumn = true;
          break;
        }
      }
    }
  }

  public acceptAllNames() {
    for (let i = 0; i < this.upload.metadata.columns.length; i++) {
      const column = this.upload.metadata.columns[i];
      if (!column.field_name) {
        this.getColumnField(i).setValue(column.column_name);
      }
    }
  }

  public async cancel(): Promise<void> {
    const t = translateObject('upload-map');
    this.dialogService
      .showWarningPopup({
        title: t['delete-upload'],
        content: t['you-have-canceled-upload'],
        confirmTitle: t['delete'],
        confirmColor: SimpleDialogButtonColor.red,
      })
      .afterClosed()
      .subscribe(async (result) => {
        if (result) {
          this.alertService.showToast(
            translate('upload-map.deleting-upload', { name: this.upload.file_name })
          );
          await this.uploadService.cancelUpload(
            this.upload.id,
            this.projectId,
            this.collectionName
          );

          this.navigateBack();
        }
      });
  }

  public navigateBack() {
    this.router.navigate([
      `groups/${this.groupService.getSelectedGroupId(true)}/projects/${this.projectId}`,
    ]);
  }

  public async deleteOnError(): Promise<void> {
    this.alertService.showToast(`Deleting upload ${this.upload.file_name}...`);
    await this.uploadService.deleteUpload(this.upload.id, this.projectId, this.collectionName);
    this.navigateBack();
  }

  public openTimezoneModal(): void {
    this.showTimezoneModal = true;
  }

  public onTimezoneModalClosed(timezone: string): void {
    this.showTimezoneModal = false;
    if (timezone) {
      this.uploadTimezone = timezone;
      this.setTimezoneOffset();
    }
  }

  private setTimezoneOffset() {
    this.timezoneService.timezone = this.uploadTimezone;
    this.uploadTimezoneOffset = 'GMT' + this.timezoneService.timezoneOffset;
    this.uploadTimezoneTooltip = `${this.uploadTimezone} (${this.uploadTimezoneOffset})`;
  }

  private getColumnField(columnIndex) {
    return this.fieldForm.get(`fieldGroup${columnIndex}`);
  }
}
UploadMapComponent.initialize();
